import React, { useState, useEffect } from "react";
import { Box, Modal, Typography, IconButton } from "@mui/material";
import { styles } from "./stylesReactionsModal";
import CustomSelect from "../../CustomSelect/CustomSelect";
import ReactionItem from "./ReactionItem";
import { useGetPostLikesQuery, useGetPostZapsQuery } from "@/store/api/postApi";
import getHoursDifference from "@/helpers/getHoursDifference";
import RemoveImgArticle from "@/assets/svg/RemoveImgArticle";
import { formatMoney } from "@/helpers/formatMoney";

interface ReactionsModalProps {
  open: boolean;
  onClose: () => void;
  postId: string;
  likesCount: number;
  zapsCount: number;
  tabsTitles?: {
    likes: string;
    zaps: string;
  };
  initialTab?: "likes" | "zaps";
}

const ReactionsModal: React.FC<ReactionsModalProps> = ({
  open,
  onClose,
  postId,
  likesCount,
  zapsCount,
  tabsTitles = { likes: "Likes", zaps: "Zaps" },
  initialTab = "likes",
}) => {
  const [activeTab, setActiveTab] = useState<"likes" | "zaps">(initialTab);
  const [selectedSort, setSelectedSort] = useState<string[]>([]);

  const { data: likesData, isLoading: isLikesLoading } = useGetPostLikesQuery(
    { uid: postId, page: 1, limit: 50 },
    { skip: !open }
  );

  const sortBy = selectedSort[0] === "Sort by Sats" ? "amount" : "recent";

  const {
    data: zapsData,
    isLoading: isZapsLoading,
    refetch: refetchZaps,
  } = useGetPostZapsQuery(
    {
      uid: postId,
      page: 1,
      limit: 50,
      sortBy,
      order: "desc",
    },
    { skip: !open }
  );

  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (activeTab === "zaps" && open) {
      refetchZaps();
    }
  }, [selectedSort, activeTab, open, refetchZaps]);

  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  const isLoading = isLikesLoading || isZapsLoading;

  const likes = activeTab === "likes" ? (likesData?.data ?? []) : [];
  const zaps = activeTab === "zaps" ? (zapsData?.data ?? []) : [];

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.modalContainer}>
        <Box sx={styles.modalContent}>
          <Box sx={styles.modalHeader}>
            <Typography sx={styles.headerText}>Reactions</Typography>
            <IconButton sx={styles.closeBtn} onClick={onClose}>
              <RemoveImgArticle size={24} />
            </IconButton>
          </Box>

          <Box sx={styles.reactionsContent}>
            <Box sx={styles.reactionsHeader}>
              <Box sx={styles.tabsWrapper}>
                <Box
                  sx={activeTab === "likes" ? styles.tabActive : styles.tabInactive}
                  onClick={() => setActiveTab("likes")}
                >
                  {tabsTitles.likes} ({likesCount.toLocaleString()})
                </Box>

                <Box
                  sx={activeTab === "zaps" ? styles.tabActive : styles.tabInactive}
                  onClick={() => setActiveTab("zaps")}
                >
                  {tabsTitles.zaps} ({zapsCount.toLocaleString()})
                </Box>
              </Box>

              <Box sx={styles.selectWrapper}>
                <CustomSelect
                  options={["Sort by USDC", "Most Recent"]}
                  selected={selectedSort}
                  onChange={setSelectedSort}
                  width="142px"
                  height={36}
                  placeholder="Sort by USDC"
                  checkbox={false}
                  multiple={false}
                  checkblack={true}
                />
              </Box>
            </Box>

            <Box sx={styles.reactionsBody} className="hide-scrollbar">
              {isLoading ? (
                <Typography sx={{ color: "#9A9FA5", textAlign: "center", mt: 4 }}>
                  Loading...
                </Typography>
              ) : activeTab === "likes" && likes.length === 0 ? (
                <Typography sx={{ color: "#9A9FA5", textAlign: "center", mt: 4 }}>
                  No reactions yet
                </Typography>
              ) : activeTab === "zaps" && zaps.length === 0 ? (
                <Typography sx={{ color: "#9A9FA5", textAlign: "center", mt: 4 }}>
                  No reactions yet
                </Typography>
              ) : (
                <>
                  {activeTab === "likes" &&
                    likes.map((item) => (
                      <ReactionItem
                        key={item.uid}
                        type="like"
                        username={item.user.username}
                        time={getHoursDifference(item.createdAt)}
                        amount={`${formatMoney(item.amount)} USDC`}
                        // amountUsd={
                        //   usdPerSat > 0 ? `(≈$${(180 * usdPerSat).toFixed(2)} USD)` : undefined
                        // }
                        avatar={item.user.image?.url ?? null}
                        uid={item.user.uid}
                      />
                    ))}

                  {activeTab === "zaps" &&
                    zaps.map((item) => (
                      <ReactionItem
                        key={item.uid}
                        type="zap"
                        username={item.user.username}
                        time={getHoursDifference(item.createdAt)}
                        amount={`${formatMoney(item.amount)} USDC`}
                        // amountUsd={
                        //   usdPerSat > 0
                        //     ? `(≈$${(item.amount * usdPerSat).toFixed(2)} USD)`
                        //     : undefined
                        // }
                        comment={item.message}
                        avatar={item.user.image?.url ?? null}
                        uid={item.user.uid}
                      />
                    ))}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ReactionsModal;
